/**
 * The home hero visual: an authored circular-economy diagram.
 *
 * The approved mock-up put a trade-fair handshake photo here. The brief asked
 * for a visual that refers to the circular economy itself, and the delivered
 * photo set contains only trade-fair photography — so this is drawn rather than
 * shot: a closed loop of five numbered stages (collection → sorting →
 * processing → secondary raw material → re-use) feeding back into collection.
 *
 * Inline SVG rather than an image file because it must follow the theme (it
 * paints with the design tokens), stays sharp at any size, costs ~2 kB, and
 * carries no licensing question.
 */
import { el } from "../core/dom.js";
import type { AppContext } from "../core/context.js";

interface Stage {
	num: string;
	label: string;
	/** Angle in degrees, measured clockwise from the top of the ring. */
	angle: number;
}

const STAGES_PL = ["Zbiórka", "Sortowanie", "Przetwarzanie", "Surowiec wtórny", "Ponowne użycie"];
const STAGES_EN = ["Collection", "Sorting", "Processing", "Secondary material", "Re-use"];

const CENTER_PL = ["Obieg", "zamknięty"];
const CENTER_EN = ["Closed", "loop"];

const CX = 260;
const CY = 190;
const RING_R = 120;
const NODE_R = 26;
const LABEL_R = 158;

const rad = (deg: number): number => (deg * Math.PI) / 180;
/** Point on a circle of radius `r`, at `deg` clockwise from the top. */
const at = (deg: number, r: number): { x: number; y: number } => ({
	x: CX + r * Math.sin(rad(deg)),
	y: CY - r * Math.cos(rad(deg)),
});
const round = (n: number): string => n.toFixed(1);

/** Where a label sits relative to its node, so text never overlaps the ring. */
function anchorFor(angle: number): "start" | "middle" | "end" {
	const x = Math.sin(rad(angle));
	if (Math.abs(x) < 0.2) return "middle";
	return x > 0 ? "start" : "end";
}

export function circularDiagram(ctx: AppContext): HTMLElement {
	const locale = ctx.locale();
	const labels = locale === "en" ? STAGES_EN : STAGES_PL;
	const center = locale === "en" ? CENTER_EN : CENTER_PL;
	const copy = ctx.copy();

	const stages: Stage[] = labels.map((label, i) => ({
		num: String(i + 1).padStart(2, "0"),
		label,
		angle: i * 72,
	}));

	const parts: string[] = [];

	parts.push(
		`<svg class="mb-loop" viewBox="0 0 520 400" role="img" aria-labelledby="mb-loop-title" focusable="false">`,
		`<title id="mb-loop-title">${escapeXml(copy.home.heroFigureAlt)}</title>`,
		`<defs>`,
		`<radialGradient id="mb-loop-glow" cx="50%" cy="45%" r="55%">`,
		`<stop offset="0%" stop-color="var(--mb-accent)" stop-opacity="0.42" />`,
		`<stop offset="100%" stop-color="var(--mb-accent)" stop-opacity="0" />`,
		`</radialGradient>`,
		`</defs>`,
		`<circle cx="${CX}" cy="${CY}" r="170" fill="url(#mb-loop-glow)" />`,
		// The loop itself. Drawn as a full circle: the arrowheads carry direction.
		`<circle class="mb-loop__ring" cx="${CX}" cy="${CY}" r="${RING_R}" />`,
	);

	// Direction arrowheads, sitting on the ring midway between stages.
	for (let i = 0; i < stages.length; i += 1) {
		const angle = i * 72 + 36;
		const p = at(angle, RING_R);
		parts.push(
			`<path class="mb-loop__arrow" d="M -6 -5 L 1 0 L -6 5" transform="translate(${round(p.x)} ${round(p.y)}) rotate(${angle})" />`,
		);
	}

	// Stage nodes: a filled disc with the ordinal, and the label outside the ring.
	for (const stage of stages) {
		const node = at(stage.angle, RING_R);
		const label = at(stage.angle, LABEL_R);
		const anchor = anchorFor(stage.angle);
		parts.push(
			`<circle class="mb-loop__node" cx="${round(node.x)}" cy="${round(node.y)}" r="${NODE_R}" />`,
			`<text class="mb-loop__num" x="${round(node.x)}" y="${round(node.y)}" text-anchor="middle" dominant-baseline="central">${stage.num}</text>`,
			`<text class="mb-loop__label" x="${round(label.x)}" y="${round(label.y)}" text-anchor="${anchor}" dominant-baseline="central">${escapeXml(stage.label)}</text>`,
		);
	}

	// Centre wordmark.
	const centreTop = center[0] ?? "";
	const centreBottom = center[1] ?? "";
	parts.push(
		`<text class="mb-loop__center" x="${CX}" y="${CY - 12}" text-anchor="middle">${escapeXml(centreTop)}</text>`,
		`<text class="mb-loop__center" x="${CX}" y="${CY + 16}" text-anchor="middle">${escapeXml(centreBottom)}</text>`,
		`</svg>`,
	);

	const figure = el("figure", { class: "mb-hero__figure" });
	// Trusted markup: every dynamic value above is escaped, none of it is remote.
	figure.innerHTML = parts.join("");
	figure.appendChild(
		el("figcaption", { class: "mb-hero__figcaption" }, copy.home.heroFigureCaption),
	);
	return figure;
}

function escapeXml(value: string): string {
	return value
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")
		.replace(/"/g, "&quot;");
}
